import React from 'react';
import { Box, useTheme } from '@mui/material';

/**
 * MediaPanel
 * ----------
 * Renders the image/video panel used by all 4 layout types.
 * Falls back to a branded gradient when mediaUrl is null.
 *
 * Props:
 *   mediaUrl            — image or video URL (nullable)
 *   mediaType           — 'image' | 'video' | null
 *   overlayOpacity      — 0–1 darkness of the overlay (default 0.45)
 *   primaryColor        — used for gradient fallback
 *   secondaryColor      — used for gradient fallback
 *   children            — text/content rendered inside (bottom-left or centered)
 *   contentPosition     — 'bottom-left' | 'bottom-right' | 'center' | 'none'
 *   sx                  — extra sx props on the outer Box
 */
const MediaPanel = ({
  mediaUrl,
  mediaType = 'image',
  overlayOpacity = 0.45,
  primaryColor,
  secondaryColor,
  children,
  contentPosition = 'bottom-left',
  sx = {},
}) => {
  const theme = useTheme();
  const primary   = primaryColor   || theme.palette.primary.main;
  const secondary = secondaryColor || theme.palette.secondary.main;

  const isVideo = mediaType === 'video' || (mediaUrl && /\.(mp4|webm|ogg)(\?|$)/i.test(mediaUrl));

  const gradientFallback = `linear-gradient(135deg, ${'#F3F7FB'} 0%, ${'white'} 60%, ${'#F3F7FB'}cc 100%)`;

  const alignMap = {
    'bottom-left':  { alignItems: 'flex-end',   justifyContent: 'flex-start' },
    'bottom-right': { alignItems: 'flex-end',   justifyContent: 'flex-end'   },
    'center':       { alignItems: 'center',      justifyContent: 'center'    },
    'none':         { alignItems: 'flex-end',   justifyContent: 'flex-start' },
  };
  const align = alignMap[contentPosition] || alignMap['bottom-left'];

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        ...align,
        background: mediaUrl && !isVideo ? 'none' : gradientFallback,
        ...sx,
      }}
    >
      {/* Background image */}
      {mediaUrl && !isVideo && (
        <Box
          component="img"
          src={mediaUrl}
          alt=""
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
          }}
        />
      )}

      {/* Background video */}
      {mediaUrl && isVideo && (
        <Box
          component="video"
          src={mediaUrl}
          autoPlay
          muted
          loop
          playsInline
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
          }}
        />
      )}

      {/* Dark overlay for legibility */}
      {mediaUrl && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: `rgba(0,0,0,${overlayOpacity})`,
          }}
        />
      )}

      {/* Content slot */}
      {contentPosition !== 'none' && children && (
        <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 3, md: 5 }, width: '100%' }}>
          {children}
        </Box>
      )}
    </Box>
  );
};

export default MediaPanel;
